import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowRight, FiPlay, FiPause, FiDollarSign, FiFileText, FiUsers, FiCpu, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { useModal } from '../services/ModalService';
import './Hero.css';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const { openContactModal } = useModal();

  const slides = [
    {
      id: 1,
      icon: <FiDollarSign />,
      title: "Enterprise Bookkeeping & CFO Services",
      subtitle: "Strategic Financial Operations",
      description: "Enhance your CPA firm's service offerings with our comprehensive bookkeeping and CFO-level advisory support. We provide white-label financial operations that help your firm deliver exceptional value to clients while scaling your practice efficiently.",
      features: ["White-Label Financial Reporting", "Client Cash Flow Analysis", "Budget Planning Support", "Scalable Service Delivery"],
      cta: "Schedule Consultation",
      bgGradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
    },
    {
      id: 2,
      icon: <FiFileText />,
      title: "Corporate Tax Strategy & Compliance",
      subtitle: "Business Tax Optimization",
      description: "Expand your CPA firm's tax practice with specialized corporate tax support and advanced planning strategies. Our tax specialists work alongside your team to handle complex corporate structures, multi-state compliance, and strategic tax optimization for your clients.",
      features: ["Advanced Tax Planning Support", "Multi-State Compliance Assistance", "R&D Credit Specialization", "Complex Entity Structures"],
      cta: "Optimize Taxes",
      bgGradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
    },
    {
      id: 3,
      icon: <FiUsers />,
      title: "Enterprise Payroll & HR Systems",
      subtitle: "Scalable Workforce Management",
      description: "Strengthen your CPA firm's payroll services with enterprise-grade processing capabilities. We provide the technology and expertise to help your firm offer comprehensive payroll and HR solutions that scale with your clients' growing workforce needs.",
      features: ["Multi-Client Payroll Platform", "HR Compliance Support", "Benefits Administration Tools", "Client Self-Service Portals"],
      cta: "Scale Workforce",
      bgGradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
    },
    {
      id: 4,
      icon: <FiUsers />,
      title: "Executive & Technical Recruitment",
      subtitle: "Strategic Talent Acquisition",
      description: "Build high-performing teams with our specialized recruitment services focused on executive leadership and technical expertise. We connect growing companies with top-tier talent to accelerate business growth and innovation.",
      features: ["C-Suite Executive Search", "Technical Team Building", "Contractor-to-Hire Programs", "Talent Pipeline Development"],
      cta: "Build Team",
      bgGradient: "linear-gradient(135deg, #fa709a 0%,rgb(185, 165, 47) 100%)"
    },
    {
      id: 5,
      icon: <FiCpu />,
      title: "Enterprise AI & Digital Transformation",
      subtitle: "Strategic Technology Solutions",
      description: "Accelerate your digital transformation with custom AI solutions designed for enterprise needs. We help businesses leverage artificial intelligence and machine learning to optimize operations, enhance decision-making, and drive competitive advantage.",
      features: ["Custom AI Development", "Process Automation", "Data Strategy & Analytics", "Digital Transformation Consulting"],
      cta: "Transform Business",
      bgGradient: "linear-gradient(135deg,rgb(72, 128, 125) 0%, #fed6e3 100%)"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const slideVariants = {
    enter: {
      x: 1000,
      opacity: 0
    },
    center: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    },
    exit: {
      x: -1000,
      opacity: 0,
      transition: {
        duration: 0.8,
        ease: "easeIn"
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  // Auto-transition slides every 5 seconds when playing
  useEffect(() => {
    if (!isPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [slides.length, isPlaying]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const currentSlideData = slides[currentSlide];

  return (
    <section 
      className="hero"
      style={{ background: currentSlideData.bgGradient }}
    >
      <div className="hero-background">
        <div className="floating-element element-1"></div>
        <div className="floating-element element-2"></div>
        <div className="floating-element element-3"></div>
      </div>
      
      <div className="container">
        <AnimatePresence mode="wait">
          <motion.div 
            key={currentSlide}
            className="hero-content"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            <motion.div className="hero-badge" variants={itemVariants}>
              <span>{currentSlideData.subtitle}</span>
            </motion.div>

            <motion.h1 className="hero-title" variants={itemVariants}>
              {currentSlideData.title}
            </motion.h1>

            <motion.p className="hero-subtitle" variants={itemVariants}>
              {currentSlideData.description}
            </motion.p>

            <motion.div className="hero-features" variants={itemVariants}>
              {currentSlideData.features.map((feature, index) => (
                <div key={index} className="feature-item">
                  <span className="feature-check">✓</span>
                  <span>{feature}</span>
                </div>
              ))}
            </motion.div>

            <motion.div className="hero-actions" variants={itemVariants}>
              <button 
                className="btn-primary hero-btn"
                onClick={() => {
                  console.log('Hero button clicked!', currentSlideData.title);
                  openContactModal(
                    currentSlideData.title,
                    `Hero Section - ${currentSlideData.cta}`
                  );
                }}
              >
                {currentSlideData.cta} <FiArrowRight />
              </button>
            </motion.div>
          </motion.div>
        </AnimatePresence>

        <AnimatePresence mode="wait">
          <motion.div 
            key={`visual-${currentSlide}`}
            className="hero-visual"
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
          >
            <div className="hero-slide-card">
              <div className="slide-icon">
                {currentSlideData.icon}
              </div>
              <h2>{currentSlideData.title}</h2>
              <p>{currentSlideData.subtitle}</p>
              
              <div className="slide-stats">
                <div className="stat-item">
                  <h3 className="gradient-text">25+</h3>
                  <p>Business Clients</p>
                </div>
                <div className="stat-item">
                  <h3 className="gradient-text">99%</h3>
                  <p>Client Retention</p>
                </div>
                <div className="stat-item">
                  <h3 className="gradient-text">3+</h3>
                  <p>Years Experience</p>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation Controls */}
        <div className="hero-navigation">
          <button className="nav-btn prev-btn" onClick={goToPrevious}>
            <FiChevronLeft />
          </button>
          
          <div className="slide-indicators">
            {slides.map((_, index) => (
              <button
                key={index}
                className={`indicator ${index === currentSlide ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
              />
            ))}
          </div>

          <button 
            className={`nav-btn play-pause-btn ${isPlaying ? 'playing' : 'paused'}`} 
            onClick={togglePlayPause}
            title={isPlaying ? 'Pause slideshow' : 'Play slideshow'}
          >
            {isPlaying ? <FiPause /> : <FiPlay />}
          </button>
          
          <button className="nav-btn next-btn" onClick={goToNext}>
            <FiChevronRight />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Hero; 